import { cn } from "src/utils/Functions"
import {
  parseAllVariableTags,
  variableAttrsToDataAttrs,
  escapeHtml as xmlEscapeHtml,
  type ContractVariable,
  type ContractVariableType,
  type GetContractResponseContent,
} from 'src/types/contract.types'

export interface TermProps {
  id: string
  type: ContractVariableType
  level?: number
  instance?: string
  children: React.ReactNode
  isHighlighted?: boolean
  variableData?: ContractVariable
  onClick?: (termId: string) => void
}

// React component for rendering terms
export const Term: React.FC<TermProps> = ({
  id,
  type,
  level,
  instance,
  children,
  isHighlighted = false,
  variableData,
  onClick
}) => {
  const baseClasses = "variable-term p-2 inline-block rounded-none transition-all duration-200 cursor-pointer"
  
  // different styling based on variable type and level
  let typeClasses = ""
  if (type === "discovered_term") {
    typeClasses = "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30"
  } else if (type === "external_term") {
    const termLevel = level || 1
    if (termLevel >= 6) {
      typeClasses = "bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30"
    } else if (termLevel >= 4) {
      typeClasses = "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 border border-orange-500/30"
    } else {
      typeClasses = "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 border border-yellow-500/30"
    }
  } else if (type === "internal_citation") {
    typeClasses = "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
  } else if (type === "eq_term") {
    typeClasses = "bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-500/30"
  } else {
    // default styles
    typeClasses = "bg-white/10 text-white hover:bg-white/20 border border-white/20"
  }
  
  // Highlighted term gets special styling
  const highlightClasses = isHighlighted 
    ? "!bg-indigo-600 !text-white" 
    : ""
  
  const classString = cn(baseClasses, highlightClasses)
  const title = variableData?.value || ''

  const handleClick = () => {
    if (onClick) {
      // Pass termId:instance format if instance exists
      const termIdentifier = instance ? `${id}:${instance}` : id
      onClick(termIdentifier)
    }
  }

  return (
    <button
      className={classString}
      data-term-id={id}
      data-type={type}
      data-level={level}
      data-instance={instance}
      title={title}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}

// HTML escape function (using shared utility for consistency)
const escapeHtml = xmlEscapeHtml

/**
 * Transform <variable> tags to <span> tags with data-* attributes
 * Uses shared XML parsing utilities to ensure consistency
 */
export function transformVariables(input: string): string {
  // Use shared parser with instance preservation from backend
  const tags = parseAllVariableTags(input, { preserveInstance: true })

  let result = input

  // Replace each tag from back to front to maintain correct offsets
  for (let i = tags.length - 1; i >= 0; i--) {
    const tag = tags[i]

    // Convert to data-* attributes using shared utility
    const dataAttrs = variableAttrsToDataAttrs(tag.attributes)
    const attrString = Object.entries(dataAttrs)
      .map(([key, val]) => `${key}="${escapeHtml(val)}"`)
      .join(' ')

    const spanTag = `<span ${attrString}>${escapeHtml(tag.innerText)}</span>`

    // Replace in the string
    result = result.substring(0, tag.startIndex) + spanTag + result.substring(tag.endIndex)
  }

  return result
}

/**
 * Alternative React-based approach for transforming variables
 * Uses shared XML parsing utilities for consistency
 */
export function transformVariablesToReact(
  input: string,
  highlightedTerm: string | null,
  variableMap: Map<string, ContractVariable>,
  onTermClick?: (termId: string) => void
): React.ReactNode[] {
  // Use shared parser with instance preservation
  const tags = parseAllVariableTags(input, { preserveInstance: true })
  const parts: React.ReactNode[] = []
  let lastIndex = 0

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]

    // Add text before this tag
    if (tag.startIndex > lastIndex) {
      parts.push(input.slice(lastIndex, tag.startIndex))
    }

    const variableId = tag.attributes.id
    const variableType = tag.attributes.type
    const level = tag.attributes.level
    const instance = tag.attributes.instance
    const variable = variableMap.get(variableId)

    if (variableId && variable) {
      // Support both "termId:instance" format and plain termId for highlighting
      const highlightedTermId = highlightedTerm?.includes(':')
        ? highlightedTerm.split(':')[0]
        : highlightedTerm
      const highlightedInstance = highlightedTerm?.includes(':')
        ? highlightedTerm.split(':')[1]
        : null

      const isHighlighted = highlightedInstance
        ? (highlightedTermId === variableId && highlightedInstance === (instance || '1'))
        : (highlightedTerm === variableId)

      parts.push(
        <Term
          key={`${tag.startIndex}-${variableId}-${instance || '1'}`}
          id={variableId}
          type={variableType}
          level={level}
          instance={instance}
          isHighlighted={isHighlighted}
          variableData={variable}
          onClick={onTermClick}
        >
          {tag.innerText}
        </Term>
      )
    } else {
      // Render as gray non-clickable term if no variable data found
      parts.push(
        <span
          key={`${tag.startIndex}-unknown-${instance || '1'}`}
          className="variable-term [line-height:1] inline rounded-none bg-gray-500/15 text-gray-400 border border-gray-500/20"
          data-type={variableType}
          data-level={level}
          data-instance={instance}
        >
          {tag.innerText}
        </span>
      )
    }

    lastIndex = tag.endIndex
  }

  // Add remaining text after the last tag
  if (lastIndex < input.length) {
    parts.push(input.slice(lastIndex))
  }

  return parts
}

type ContractViewerTheme = "dark" | "light"

interface ContractViewerContentOptions {
  theme?: ContractViewerTheme
  contractTextOverride?: string
}

type ContractViewerThemeClasses = {
  emptyState: string
  root: string
  unknownTerm: string
  baseTerm: string
  discoveredTerm: string
  internalCitationTerm: string
  eqTerm: string
  externalTermLow: string
  externalTermMid: string
  externalTermHigh: string
  defaultTerm: string
  highlightedTerm: string
}

const CONTRACT_VIEWER_THEME_CLASSES: Record<ContractViewerTheme, ContractViewerThemeClasses> = {
  dark: {
    emptyState: "text-white/60",
    root: "text-white/60 [&>p]:mb-4",
    unknownTerm: "variable-term [line-height:1] inline-block rounded-none bg-gray-500/15 text-gray-400 border border-gray-500/20",
    baseTerm: "variable-term [line-height:1] inline-block py-1 px-3 bg-white/10 rounded-full transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/70",
    discoveredTerm: "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30",
    internalCitationTerm: "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30",
    eqTerm: "bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-500/30",
    externalTermLow: "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 border border-yellow-500/30",
    externalTermMid: "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 border border-orange-500/30",
    externalTermHigh: "bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30",
    defaultTerm: "bg-white/10 text-white hover:bg-white/20 border border-white/20",
    highlightedTerm: "!bg-indigo-600 !text-white !border-indigo-400",
  },
  light: {
    emptyState: "text-slate-500",
    root: "text-slate-700 [&>p]:mb-4 [&>p]:leading-relaxed",
    unknownTerm: "variable-term [line-height:1.1] inline-block rounded-md bg-slate-100 text-slate-500 border border-slate-200",
    baseTerm: "variable-term [line-height:1.1] inline-block py-0.5 px-2 rounded-md border transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
    discoveredTerm: "bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200",
    internalCitationTerm: "bg-violet-100 text-violet-700 hover:bg-violet-200 border-violet-200",
    eqTerm: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200",
    externalTermLow: "bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200",
    externalTermMid: "bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200",
    externalTermHigh: "bg-red-100 text-red-800 hover:bg-red-200 border-red-200",
    defaultTerm: "bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-300",
    highlightedTerm: "!bg-indigo-600 !text-white !border-indigo-500",
  },
}

export const getContractViewerContent = (
  highlightedTerm: string | null,
  contract?: GetContractResponseContent,
  onTermClick?: (termId: string) => void,
  options: ContractViewerContentOptions = {}
) => {
  console.debug('Contract viewer rendering with highlightedTerm:', highlightedTerm)
  const theme = options.theme ?? "dark"
  const themeClasses = CONTRACT_VIEWER_THEME_CLASSES[theme]
  
  // v1 API: Contract text is in contractExtraction.contractText.markedUpContract.text
  const contractText = options.contractTextOverride ?? contract?.contractExtraction?.contractTexts?.taggedText?.text
  
  if (!contract || !contractText) {
    return <div className={themeClasses.emptyState}>No contract text available</div>
  }

  // Get variables from the contract if available
  const contractVariables = contract.contractExtraction?.variables || {}
  
  // Parse the contract text using the transformVariables function
  // This preserves backend instance numbers
  const transformedHtml = transformVariables(contractText)

  // Function to process the transformed HTML and add styling classes
  const processContractHTML = (htmlString: string) => {
    return htmlString.replace(
      /<span ([^>]+)>/g,
      (_match, attributes) => {
        // Extract attributes from the span tag
        const idMatch = attributes.match(/data-id="([^"]+)"/)
        const instanceMatch = attributes.match(/data-instance="([^"]+)"/)
        const variableId = idMatch ? idMatch[1] : null
        const variable = variableId ? contractVariables[variableId] : null

        // Use the instance from backend (already preserved in data-instance attribute)
        const instanceNum = instanceMatch ? instanceMatch[1] : '1'
        
        // If no variable data found, style as gray non-clickable term
        if (!variable || !variableId) {
          const grayClasses = themeClasses.unknownTerm
          return `<span class="${grayClasses}" ${attributes}>`
        }
        
        // Build the class string for styling
        // Support both "termId:instance" format and plain termId
        const highlightedTermId = highlightedTerm?.includes(':') 
          ? highlightedTerm.split(':')[0] 
          : highlightedTerm
        const highlightedInstance = highlightedTerm?.includes(':') 
          ? highlightedTerm.split(':')[1] 
          : null
        
        const isHighlighted = highlightedInstance 
          ? (highlightedTermId === variableId && highlightedInstance === String(instanceNum))
          : (highlightedTerm === variableId)
        const baseClasses = themeClasses.baseTerm
        
        // Different styling based on variable type and level
        let typeClasses = ""
        if (variable.type === "discovered_term") {
          typeClasses = themeClasses.discoveredTerm
        } else if (variable.type === "external_term") {
          const level = variable.level || 1
          if (level >= 6) {
            typeClasses = themeClasses.externalTermHigh
          } else if (level >= 4) {
            typeClasses = themeClasses.externalTermMid
          } else {
            typeClasses = themeClasses.externalTermLow
          }
        } else if (variable.type === "internal_citation") {
          typeClasses = themeClasses.internalCitationTerm
        } else if (variable.type === "eq_term") {
          typeClasses = themeClasses.eqTerm
        } else {
          // Default styling
          typeClasses = themeClasses.defaultTerm
        }
        
        // Highlighted term gets special styling
        const highlightClasses = isHighlighted
          ? themeClasses.highlightedTerm
          : ""

        const classString = cn(baseClasses, typeClasses, highlightClasses)
        const title = escapeHtml(variable.value || '')

        // Keep existing data-* attributes but add styling
        return `<span class="${classString}" data-term-id="${variableId}" data-instance="${instanceNum}" title="${title}" tabindex="0" role="button" ${attributes}>`
      }
    )
  }

  const activateTerm = (target: HTMLElement) => {
    if (!target.classList.contains('variable-term') || !target.hasAttribute('data-term-id')) return
    const termId = target.getAttribute('data-term-id')
    const instance = target.getAttribute('data-instance')
    if (termId && instance && onTermClick) {
      // Pass term ID and instance as "termId:instance" format
      onTermClick(`${termId}:${instance}`)
    }
  }

  // Handle click events through event delegation
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    activateTerm(event.target as HTMLElement)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return

    const target = event.target as HTMLElement
    if (!target.classList.contains('variable-term') || !target.hasAttribute('data-term-id')) return

    event.preventDefault()
    activateTerm(target)
  }

  // Split contract text into paragraphs and process each
  const paragraphs = transformedHtml.split('\n\n').filter((p: string) => p.trim())
  
  return (
    <div className={themeClasses.root} onClick={handleClick} onKeyDown={handleKeyDown}>
      {paragraphs.map((paragraph: string, index: number) => (
        <p key={index} dangerouslySetInnerHTML={{ __html: processContractHTML(paragraph) }} />
      ))}
    </div>
  )
}
